import * as faceapi from "@vladmandic/face-api/dist/face-api.esm.js";
import moment from "moment";
import React from "react";
import Swal from "sweetalert2";
import SkeletonLoader from "../../../components/TimeClock/SkeletonLoader";
import { config } from "../../../config/index";
import { GET, newFetch, PUT } from "../../../helpers/fetch";

// components
import CapturePhoto from "./CapturePhoto";
import ConfirmLocation from "./ConfirmLocation";
import FaceVerification from "./FaceVerification";
import MultipleShiftSelect from "./MultipleShiftSelect";
import StartTimeclock from "./StartTimeclock";

import "./styles.css";
const { LOCATION_API_URL, LOCATION_API_SECRET_KEY } = config;

const ClockInOut = ({
  history,
  activeDate,
  activeSchedules,
  activeSite,
  selectedSchedule,
  setSelectedSchedule,
  activeClient,
  circleRadius,
  isInsideCircle,
  distanceTo,
  showAllShifts,
  sites,
  siteSchedules,
  isMultipleShiftAssign,
  defaultSiteData,
  t,
}) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const site_id = user?.site_associates[0]?.site_id;
  const [apiLocation, setApiLocation] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);
  const [timeClockType, setTimeClockType] = React.useState(null);
  const [imgSrc, setImgSrc] = React.useState();
  const [src, setSrc] = React.useState(null);
  const videoRef = React.useRef();
  const [startClockInOut, setStartClockInOut] = React.useState(false);
  const [attendanceLogs, setAttendanceLogs] = React.useState([]);
  const [features, setFeatures] = React.useState([]);
  const [featuresOnOff, setFeaturesOnOff] = React.useState({
    feature: null,
  });
  const [confirmLocation, setConfirmLocation] = React.useState(null);
  const [confirmCapturedPhoto, setConfirmCapturedPhoto] = React.useState(null);
  const [coordinates, setCoordinates] = React.useState(null);
  const [answers, setAnswers] = React.useState([]);
  const [group, setGroup] = React.useState([]);
  const [overNightShift, setOvernightShift] = React.useState();
  const [isWithin5Hours, setIsWithin5Hours] = React.useState();
  const [mainScheduleId, setMainScheduleId] = React.useState(null);
  const [mainAttendanceRecord, setMainAttendanceRecord] = React.useState(null);
  const [mainScheduleData, setMainScheduleData] = React.useState(null);

  React.useEffect(() => {
    if (features.length > 0 && features.every((item) => item.trail === true)) {
      setStartClockInOut((prev) => {
        if (!prev) return true;
        return prev;
      });
    }
  }, [features]);

  // Function to get the relevant schedule ID based on current time
  const getRelevantScheduleId = async () => {
    const currentTime = moment();
    const dateToday = currentTime.format("YYYY-MM-DD");
    const yesterday = currentTime
      .clone()
      .subtract(1, "day")
      .format("YYYY-MM-DD");

    // 1️⃣ First, check for today's schedule
    const todaySchedule = siteSchedules.find(
      (schedule) =>
        schedule.date === dateToday &&
        schedule.shift_id === selectedSchedule?.shift_id,
    );

    // 2️⃣ Check if there's an overnight shift from yesterday
    const yesterdaySchedule = siteSchedules.find(
      (schedule) =>
        schedule.date === yesterday &&
        schedule.shift_id === selectedSchedule?.shift_id,
    );

    if (
      yesterdaySchedule?.shift.start_time &&
      yesterdaySchedule?.shift.end_time
    ) {
      const startTime = moment(yesterdaySchedule.shift.start_time, "HH:mm:ss");
      const endTime = moment(yesterdaySchedule.shift.end_time, "HH:mm:ss");
      const isOverNightShift = endTime.isBefore(startTime);

      //console.log("isOverNightShift", isOverNightShift);

      if (isOverNightShift && todaySchedule?.id) {
        // Check if today's schedule has an attendance record
        const todayClockIn = attendanceLogs.find(
          (item) =>
            item.schedule_id === todaySchedule.id &&
            item.type === "checkin" &&
            item.date === dateToday,
        );

        // If today has a clock-in record, use today's schedule
        if (todayClockIn?.id) {
          const todayStartTime = moment(
            todaySchedule.shift.start_time,
            "HH:mm:ss",
          );
          const todayEndTime = moment(todaySchedule.shift.end_time, "HH:mm:ss");
          const isTodayOvernightShift = todayEndTime.isBefore(todayStartTime);

          return {
            scheduleId: todaySchedule.id,
            scheduleData: todaySchedule,
            date: dateToday,
            isOverNightShift: isTodayOvernightShift,
            isWithin5Hours: false,
          };
        }

        const scheduleStatus = await fetchScheduleStatus(yesterdaySchedule.id);
        if (scheduleStatus && scheduleStatus.is_complete === true) {
          return {
            scheduleId: todaySchedule.id,
            scheduleData: todaySchedule,
            date: dateToday,
            isOverNightShift: false,
            isWithin5Hours: false,
          };
        }

        // Overnight shift starts yesterday and ends today
        const shiftStartDateTime = moment(
          `${yesterday} ${yesterdaySchedule.shift.start_time}`,
          "YYYY-MM-DD HH:mm:ss",
        );

        // Since it's an overnight shift, end time is on today's date
        const shiftEndDateTime = moment(
          `${dateToday} ${yesterdaySchedule.shift.end_time}`,
          "YYYY-MM-DD HH:mm:ss",
        );

        // Allow clock out up to 5 hours after shift end time
        const fiveHoursAfterEnd = shiftEndDateTime.clone().add(5, "hours");

        //console.log("shiftStartDateTime", shiftStartDateTime);

        // Check if current time is within the valid window:
        // - After shift start time AND
        // - Before 5 hours after shift end time
        const isWithinValidWindow =
          currentTime.isAfter(shiftStartDateTime) &&
          currentTime.isBefore(fiveHoursAfterEnd);

        //console.log("isWithinValidWindow", isWithinValidWindow);

        // If we're within the valid window and shift is not complete
        if (yesterdaySchedule.is_complete !== 1 && isWithinValidWindow) {
          return {
            scheduleId: yesterdaySchedule.id,
            scheduleData: yesterdaySchedule,
            date: yesterday,
            isOverNightShift: true,
            isWithin5Hours: currentTime.isAfter(shiftEndDateTime), // True if within the 5-hour grace period
          };
        }
      }
    }

    // 3️⃣ Default to today's schedule
    if (todaySchedule?.id) {
      const startTime = moment(todaySchedule.shift.start_time, "HH:mm:ss");
      const endTime = moment(todaySchedule.shift.end_time, "HH:mm:ss");
      const isOverNightShift = endTime.isBefore(startTime);

      return {
        scheduleId: todaySchedule.id,
        scheduleData: todaySchedule,
        date: dateToday,
        isOverNightShift,
        isWithin5Hours: false,
      };
    }

    return null;
  };

  // Extract the logic from useEffect into a separate function
  const refreshScheduleData = React.useCallback(async () => {
    if (siteSchedules && selectedSchedule && siteSchedules.length > 0) {
      const relevantSchedule = await getRelevantScheduleId();

      if (relevantSchedule?.scheduleId) {
        setMainScheduleId(relevantSchedule?.scheduleId);
        setMainScheduleData(relevantSchedule?.scheduleData);
        setOvernightShift(relevantSchedule?.isOverNightShift);
        setIsWithin5Hours(relevantSchedule?.isWithin5Hours);

        if (relevantSchedule.scheduleId !== mainScheduleId) {
          fetchAttendanceLogs(relevantSchedule?.scheduleId);
        }
      }
    }
  }, [siteSchedules, selectedSchedule, attendanceLogs, mainScheduleId]); // Add dependencies

  // Update your useEffect to use this function
  React.useEffect(() => {
    refreshScheduleData();
  }, [siteSchedules, attendanceLogs, selectedSchedule]);

  // Update attendance record when logs are fetched
  React.useEffect(() => {
    if (mainScheduleId && attendanceLogs.length > 0) {
      const record = attendanceLogs.find(
        (log) => log.schedule_id === mainScheduleId && log.type === "checkin",
      );
      setMainAttendanceRecord(record || null);
    }
  }, [mainScheduleId, attendanceLogs]);

  React.useEffect(() => {
    featureHandler();
  }, [defaultSiteData, isMultipleShiftAssign]);

  React.useEffect(() => {
    if (defaultSiteData?.enable_face_recognition) {
      fetchImageSource();
    }
  }, [defaultSiteData]);

  React.useEffect(() => {
    if (startClockInOut) {
      setTimeout(() => {
        startClockingInOut();
      }, 100);
    }
  }, [startClockInOut]);

  const masterClockInOutHandler = async (type) => {
    setDisabled(true);
    setLoading(true);
    setTimeClockType(type);

    if (!["break in", "break out"].includes(type)) {
      for (let feat of features) {
        if (feat.feature === "selectMultipleShift" && !feat.trail) {
          setFeaturesOnOff({ feature: "selectMultipleShift" });
          break;
        }

        if (feat.feature === "capturePhoto" && !feat.trail) {
          setFeaturesOnOff({ feature: "capturePhoto" });
          break;
        }

        if (feat.feature === "geolocation" && !feat.trail) {
          setFeaturesOnOff({ feature: "geolocation" });
          break;
        }

        if (feat.feature === "faceRecognition" && !feat.trail) {
          enableFaceRecognition();
          break;
        }
      }
    } else {
      setStartClockInOut(true);
    }

    if (!features.length) {
      setStartClockInOut(true);
    }
  };

  const startClockingInOut = async () => {
    // Essential validations only
    if (!mainScheduleId || !mainScheduleData) {
      Swal.fire({
        title: "Schedule Error",
        text: "Schedule information is missing. Please refresh and try again.",
        icon: "error",
      });
      return;
    }

    // Validate site_id exists
    if (!mainScheduleData.shift?.site_id && !mainScheduleData.site_id) {
      Swal.fire({
        title: "Site Error",
        text: "Site information is missing. Please refresh and try again.",
        icon: "error",
      });
      return;
    }

    setLoading(true);
    setDisabled(true);

    try {
      //  Build payload - ensure all critical IDs are set
      let payload = {
        associate_ids: [user.id],
        schedule: {
          ...mainScheduleData,
          schedule_id: mainScheduleId,
          site_id: mainScheduleData.shift?.site_id || mainScheduleData.site_id, // Ensure site_id is included
          shift: {
            ...mainScheduleData.shift,
            site_id:
              mainScheduleData.shift?.site_id || mainScheduleData.site_id, // Also in shift object
          },
        },
      };

      if (confirmLocation) {
        payload.location = confirmLocation;
        payload.longitude = coordinates.Long;
        payload.latitude = coordinates.Lat;
      }

      if (defaultSiteData?.enable_geofencing) {
        const { lat, lng, name } = defaultSiteData;

        payload.latitude = lat;
        payload.longitude = lng;
        payload.location = name;

        if (lat && lng) {
          const res = await fetch(
            `${LOCATION_API_URL}${lng},${lat}${LOCATION_API_SECRET_KEY}`,
          );

          const data = await res.json();

          if (data?.features?.length) {
            payload.location = data.features[0].place_name;
          }
        }
      }

      if (answers.length > 0) {
        payload.site_checklist_answers = answers;
      }

      switch (timeClockType) {
        case "clock in":
          payload.time_in = moment().format("YYYY-MM-DD HH:mm");
          payload.translated_message = {
            thank_you_clockin: "Thank you for Clock In",
          };
          break;
        case "clock out":
          payload.time_out = moment().format("YYYY-MM-DD HH:mm");
          payload.translated_message = {
            thank_you_clockout: "Thank you for Clock Out",
          };
          break;
        case "break in":
          payload.break_in = moment().format("YYYY-MM-DD HH:mm");
          payload.translated_message = {
            break_in_success: "Thank you for Break In",
          };
          break;
        case "break out":
          payload.break_out = moment().format("YYYY-MM-DD HH:mm");
          payload.translated_message = {
            break_out_success: "Thank you for Break Out",
          };
          break;
        default:
          break;
      }

      //  API call: use mainScheduleId in URL
      const api_path = `/associate/${mainScheduleId}/clockinout_update`;
      const response = await newFetch(PUT, api_path, payload);

      if (response?.success) {
        const attendanceId = response?.data.id;

        // 🔄 Re-run schedule detection
        const updated = await getRelevantScheduleId();

        if (updated) {
          setMainScheduleId(updated.scheduleId);
          setMainScheduleData(updated.scheduleData);
        }

        // Upload captured photo if any
        if (confirmCapturedPhoto) {
          const formData = new FormData();
          formData.append("file", confirmCapturedPhoto);
          formData.append("attedance_id", attendanceId);
          await newFetch(PUT, `/upload/capture_photo`, formData, "form");
        }

        // Redirect or refresh attendance logs
        if (!["break in", "break out"].includes(timeClockType)) {
          history.push("/thank-you", {
            timeclock: moment().format("YYYY-MM-DD HH:mm"),
            timeClockType,
          });
        } else {
          setStartClockInOut(false);
          setLoading(false);
          setDisabled(false);
          fetchAttendanceLogs(mainScheduleId);
        }
      } else {
        Swal.fire({
          title: "Time Clock Error",
          text: "Unable to proceed. It seems there is a site assignment issue. Please try logging out and logging back in. If the issue persists, please contact the administrator or try again later.",
          icon: "error",
        });
        setLoading(false);
        setDisabled(false);
        setConfirmLocation(null);
        setConfirmCapturedPhoto(null);
        setCoordinates(null);
        setFeaturesOnOff({ feature: null });
        setStartClockInOut(false);
      }
    } catch (error) {
      console.error("Clock In/Out Error:", error);
      Swal.fire({
        title: "Time Clock Error",
        text:
          error.message || "An unexpected error occurred. Please try again.",
        icon: "error",
      });
      setLoading(false);
      setDisabled(false);
    }
  };

  const enableFaceRecognition = () => {
    const loadModels = async () => {
      const MODEL_URL = process.env.PUBLIC_URL + "/models";
      Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL),
      ]).then(startVideo);
    };

    if (imgSrc !== undefined && imgSrc.length > 0) {
      loadModels();
      setFeaturesOnOff({ feature: "faceRecognition" });
    } else {
      Swal.fire({
        title: "Error",
        text: `Face is not detected. Make sure you have uploaded your face id on the Employee Edit Profile or contact the admin.`,
        icon: "error",
      });
      setStartClockInOut(false);
      setLoading(false);
      setDisabled(false);
    }
  };

  const fetchImageSource = async () => {
    if (!user?.id || !site_id) return;

    const api_path = `/associate/${user.id}/site/${site_id}/img_source`;

    const response = await newFetch(GET, api_path);

    if (response.success) {
      let data = response.data;
      setImgSrc(data);
    } else {
      Swal.fire({
        title: "Error",
        text: response.message,
        icon: "error",
      });
    }
  };

  const fetchAttendanceLogs = async (id) => {
    if (!id || !user?.id) return;

    let api_path = `/associate/${user.id}/schedule/${id}/attendance_log`;
    let response = await newFetch(GET, api_path);
    if (response.success) {
      let data = response.data;
      setAttendanceLogs(data);
      await groupAttendanceLogs(data);
    }
  };

  const fetchScheduleStatus = async (scheduleId) => {
    const api_path = `/associate/schedule/${scheduleId}/status`;
    const response = await newFetch(GET, api_path);

    if (response.success) {
      return response.data; // contains is_complete
    } else {
      console.warn("Failed to fetch schedule status");
      return null;
    }
  };

  const groupAttendanceLogs = async (timeLogs) => {
    if (!timeLogs || !Array.isArray(timeLogs)) return;

    const groupedLogs = [];
    let currentRow = {
      time_in: "-",
      break_in: "-",
      break_out: "-",
      time_out: "-",
    };
    const sortedLogs = [...timeLogs].sort((a, b) => a.id - b.id);

    sortedLogs.forEach((log) => {
      const key = "punchtime";

      switch (log.type) {
        case "checkin":
          if (Object.values(currentRow).some((val) => val !== "-")) {
            groupedLogs.push(currentRow);
            currentRow = {
              time_in: "-",
              break_in: "-",
              break_out: "-",
              time_out: "-",
            };
          }
          currentRow.time_in = moment(log[key]).format("HH:mm");
          break;

        case "breakin":
          if (currentRow.break_in !== "-") {
            groupedLogs.push(currentRow);
            currentRow = {
              time_in: "-",
              break_in: "-",
              break_out: "-",
              time_out: "-",
            };
          }
          currentRow.break_in = log[key];
          break;

        case "breakout":
          currentRow.break_out = log[key];
          break;

        case "checkout":
          currentRow.time_out = moment(log[key]).format("HH:mm");
          groupedLogs.push(currentRow);
          currentRow = {
            time_in: "-",
            break_in: "-",
            break_out: "-",
            time_out: "-",
          };
          break;

        default:
          break;
      }
    });

    if (Object.values(currentRow).some((val) => val !== "-")) {
      groupedLogs.push(currentRow);
    }
    setGroup(groupedLogs);
  };

  const startVideo = async () => {
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(function (stream) {
        const video = document.querySelector("video");
        if ("srcObject" in video) {
          videoRef.current.srcObject = stream;
          setSrc(stream);
        }
      })
      .catch(function (err) {
        console.log(err.name + ": " + err.message);
      });
  };

  const isClockInToday = () => {
    return !!mainAttendanceRecord;
  };

  const isClockOutToday = () => {
    const dateToday = moment().format("YYYY-MM-DD");

    const isClockOut = attendanceLogs.find(
      (item) =>
        item.schedule_id === mainScheduleId &&
        item.type === "checkout" &&
        item.date === dateToday,
    );

    return isClockOut?.id ? true : false;
  };

  const featureHandler = async () => {
    const activeFeatures = [];

    if (isMultipleShiftAssign) {
      activeFeatures.push({
        feature: "selectMultipleShift",
        trail: false,
      });
    }

    if (defaultSiteData?.enable_capture_photo) {
      activeFeatures.push({
        feature: "capturePhoto",
        trail: false,
      });
    }

    if (defaultSiteData?.enable_geolocation) {
      activeFeatures.push({
        feature: "geolocation",
        trail: false,
      });
    }

    if (defaultSiteData?.enable_face_recognition) {
      activeFeatures.push({
        feature: "faceRecognition",
        trail: false,
      });
    }

    setFeatures(activeFeatures);
  };

  return (
    <React.Fragment>
      {mainScheduleId || isMultipleShiftAssign ? (
        <StartTimeclock
          user={user}
          isClockInToday={isClockInToday}
          masterClockInOutHandler={masterClockInOutHandler}
          loading={loading}
          selectedSchedule={mainScheduleData}
          attendanceLogs={attendanceLogs}
          isClockOutToday={isClockOutToday}
          featuresOnOff={featuresOnOff}
          activeSite={activeSite}
          setAnswers={setAnswers}
          answers={answers}
          group={group}
          defaultSiteData={defaultSiteData}
          overNightShift={overNightShift}
          refreshScheduleData={refreshScheduleData}
          isWithin5Hours={isWithin5Hours}
          t={t}
        />
      ) : (
        <SkeletonLoader />
      )}

      <MultipleShiftSelect
        featuresOnOff={featuresOnOff}
        setFeaturesOnOff={setFeaturesOnOff}
        masterClockInOutHandler={masterClockInOutHandler}
        timeClockType={timeClockType}
        setSelectedSchedule={setSelectedSchedule}
        activeSchedules={activeSchedules}
        setFeatures={setFeatures}
      />

      <CapturePhoto
        featuresOnOff={featuresOnOff}
        setFeaturesOnOff={setFeaturesOnOff}
        setConfirmLocation={setConfirmLocation}
        masterClockInOutHandler={masterClockInOutHandler}
        setConfirmCapturedPhoto={setConfirmCapturedPhoto}
        timeClockType={timeClockType}
        setFeatures={setFeatures}
        t={t}
      />

      <ConfirmLocation
        featuresOnOff={featuresOnOff}
        setConfirmLocation={setConfirmLocation}
        masterClockInOutHandler={masterClockInOutHandler}
        setApiLocation={setApiLocation}
        apiLocation={apiLocation}
        setFeaturesOnOff={setFeaturesOnOff}
        coordinates={coordinates}
        setCoordinates={setCoordinates}
        timeClockType={timeClockType}
        activeClient={activeClient}
        isInsideCircle={isInsideCircle}
        distanceTo={distanceTo}
        circleRadius={circleRadius}
        sites={sites}
        setFeatures={setFeatures}
        defaultSiteData={defaultSiteData}
        t={t}
      />

      <FaceVerification
        user={user}
        type={timeClockType}
        imgSrc={imgSrc}
        src={src}
        videoRef={videoRef}
        faceapi={faceapi}
        setStartClockInOut={setStartClockInOut}
        featuresOnOff={featuresOnOff}
        t={t}
      />
    </React.Fragment>
  );
};

export default ClockInOut;
